import React, { useEffect, useRef, useState } from 'react';
import { fetchPromoWheel, spinPromoWheel } from '../../../api/promoCodesApi';
import { getApiErrorText } from '../../../utils/apiResponse';
import { promoDateLabel } from '../../../utils/promoDates';
import PromoWheelOffer from './PromoWheelOffer';
import { DEFAULT_WINNING_DISCOUNTS, SPIN_DURATION_MS, winningRotation } from '../../../utils/promoWheel';
import '../../../styles/promo-wheel.css';

const statusLabels = { AVAILABLE: 'Готовий до використання', USED: 'Використано', EXPIRED: 'Термін дії вийшов', DISABLED: 'Промокод вимкнено' };

export default function PromoCodesTab({ active = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [copied, setCopied] = useState('');
  const request = useRef(0);
  const busy = useRef(false);
  const timer = useRef(null);
  const codeInput = useRef(null);

  useEffect(() => {
    if (!active) return;
    const sequence = ++request.current;
    setLoading(true); setData(null); setError(''); setCopied(''); setSpinning(false); setRotation(0); busy.current = false;
    fetchPromoWheel().then(result => {
      if (sequence === request.current) setData(result);
    }).catch(reason => {
      if (sequence === request.current) setError(getApiErrorText(reason));
    }).finally(() => { if (sequence === request.current) setLoading(false); });
    return () => {
      request.current += 1;
      clearTimeout(timer.current);
      busy.current = false;
    };
  }, [active, refresh]);

  const spin = async () => {
    if (busy.current || !data?.canSpin) return;
    busy.current = true; setSpinning(true); setError('');
    const sequence = request.current;
    try {
      const result = await spinPromoWheel();
      if (sequence !== request.current) return;
      if (!result?.reward || !DEFAULT_WINNING_DISCOUNTS.includes(result.reward.discountValue)) throw new Error('Не вдалося показати виграш. Оновіть вкладку.');
      setRotation(winningRotation(result.reward.discountValue));
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      timer.current = setTimeout(() => {
        if (sequence !== request.current) return;
        setData(result); setSpinning(false); busy.current = false;
      }, reducedMotion ? 0 : SPIN_DURATION_MS + 100);
    } catch (reason) {
      if (sequence !== request.current) return;
      setError(getApiErrorText(reason));
      // A lost response may still have saved a reward. The next POST is idempotent as well.
      try {
        const latest = await fetchPromoWheel();
        if (sequence === request.current) { setData(latest); if (latest?.reward) setError(''); }
      } catch (_) { /* Keep the actionable original error and a retry button. */ }
      finally { if (sequence === request.current) { setSpinning(false); busy.current = false; } }
    }
  };

  const copy = async () => {
    const sequence = request.current;
    try {
      await navigator.clipboard.writeText(data.reward.code);
      if (sequence === request.current) setCopied('Промокод скопійовано');
    } catch (_) {
      if (sequence !== request.current) return;
      codeInput.current?.focus(); codeInput.current?.select();
      setCopied('Скопіюйте виділений код із поля');
    }
  };

  const isInvalidReward = ['EXPIRED', 'DISABLED'].includes(data?.reward?.status);

  if (!active) return null;
  return <section className='promo-wheel-tab personal-cabinet-tab-content' aria-labelledby='my-promo-codes-title'>
    <h5 id='my-promo-codes-title' className='font-weight-bold mb-3 text-center'>Мої промокоди</h5>
    {loading ? <p role='status'>Завантажуємо промокоди…</p> : <>
      {error && <div className='alert alert-danger' role='alert'>{error}</div>}
      {data?.reward ? <article className={`promo-wheel-reward${data.reward.status === 'USED' ? ' promo-wheel-reward--used' : ''}${isInvalidReward ? ' promo-wheel-reward--invalid' : ''}`} aria-label='Ваш промокод'>
        <div className='promo-wheel-reward-value'>
          <div className='promo-wheel-discount'>{data.reward.discountValue}<span>%</span></div>
          <span className='promo-wheel-discount-caption'>{data.reward.status === 'USED' ? 'знижку використано' : 'додаткова знижка'}</span>
        </div>
        <div className='promo-wheel-reward-content'>
          <div className='promo-wheel-reward-top'>
            <span className='promo-wheel-reward-label'>Персональний промокод</span>
            <span className={`promo-wheel-status promo-wheel-status--${data.reward.status}`} role='status'>
              {data.reward.status === 'USED' && <i className='bi bi-check-circle-fill' aria-hidden='true' />}
              {statusLabels[data.reward.status] || data.reward.status}
            </span>
          </div>
          <div className='promo-wheel-code'>
            <input ref={codeInput} aria-label='Виграний промокод' readOnly value={data.reward.code} onFocus={event => event.target.select()} />
            {data.reward.status === 'AVAILABLE' && <button type='button' className='btn btn-dark' onClick={copy}><i className='bi bi-copy mr-2' aria-hidden='true' />Скопіювати</button>}
          </div>
          <p role='status' className='promo-wheel-copy-status'>{copied}</p>
          <div className='promo-wheel-reward-meta'>
            <span>{data.reward.status === 'EXPIRED' ? 'Діяв до' : 'Діє до'} <strong>{promoDateLabel(data.reward.expiresAt)}</strong></span>
            <span>На одне замовлення</span>
          </div>
          {!isInvalidReward && <p className='promo-wheel-reward-hint'>{data.reward.status === 'USED'
            ? 'Знижку вже застосовано до вашого замовлення.'
            : data.reward.status === 'AVAILABLE' ? 'Введіть код під час оформлення. Доступний лише вашому акаунту.' : 'Цей промокод більше недоступний для застосування.'}</p>}
        </div>
        {data.reward.status === 'USED' ? <div className='promo-wheel-used-notice' role='status'>
          <i className='bi bi-check-circle-fill' aria-hidden='true' />
          <div><strong>Промокод уже використано</strong><span>Повторне застосування недоступне.</span></div>
        </div> : data.reward.status === 'AVAILABLE' && <div className='promo-wheel-benefits' aria-label='Переваги промокоду'>
          <span><i className='bi bi-check-circle-fill' aria-hidden='true' />Сумується з персональною знижкою</span>
          <span><i className='bi bi-check-circle-fill' aria-hidden='true' />Діє на акційні товари</span>
        </div>}
      </article> : data?.canSpin ? <PromoWheelOffer campaign={data.campaign} spinning={spinning} rotation={rotation} onSpin={spin} /> : !error && <div className='promo-wheel-empty'>
        <p className='text-muted mb-0'>У вас поки немає промокодів.</p></div>}
      {error && <button type='button' className='btn btn-outline-secondary mt-3' disabled={spinning} onClick={() => setRefresh(value => value + 1)}>Оновити промокоди</button>}
    </>}
  </section>;
}
